import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AlertTriangle, FileText, Upload, CheckCircle2, Loader, XCircle } from 'lucide-react';
import { enfantsApi, piecesJointesApi } from '../../utils/api';
import SignatureButton from '../../components/Signature/SignatureButton';
import EnvoiDocumentButton from '../../components/EnvoiDocumentButton';

/**
 * Rappel « avenant à faire signer » : reste affiché tant que l'avenant signé n'est pas reçu.
 * Validation par signature électronique, par dépôt du document signé (vérifié par l'IA),
 * ou manuellement par la direction.
 */
export default function AvenantSuivi({ enfantId, refreshKey, emailFamille, nomFamille, onValide }) {
  const [attente, setAttente] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);   // { type: 'ok'|'ko'|'error', text, verdict? }
  const fileRef = useRef(null);

  const load = useCallback(() => {
    enfantsApi.getAvenantStatut(enfantId).then(r => setAttente(r.data.en_attente)).catch(() => setAttente(null));
  }, [enfantId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const deposer = async (file) => {
    if (!file) return;
    setBusy(true); setRes(null);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('entite_type', 'enfant');
      fd.append('entite_id', String(enfantId));
      fd.append('categorie', 'contrat');
      const up = await piecesJointesApi.upload(fd);
      const { data } = await enfantsApi.verifierAvenant(enfantId, up.data.id);
      const v = data.verdict;
      if (v.conforme) {
        setRes({ type: 'ok', text: `Avenant signé reconnu : ${v.resume || 'document conforme'}. Le rappel est levé.` });
        load(); onValide && onValide();
      } else {
        const manque = [!v.est_avenant && "ce n'est pas un avenant au contrat d'accueil", !v.enfant_correspond && "il ne semble pas concerner cet enfant", !v.signe_famille && 'aucune signature de la famille détectée'].filter(Boolean);
        setRes({ type: 'ko', text: `Avenant non validé : ${manque.join(' ; ')}.`, verdict: v });
      }
    } catch (err) { setRes({ type: 'error', text: err.response?.data?.error || 'Dépôt ou analyse impossible.' }); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const validerManuellement = async () => {
    if (!window.confirm("Confirmer que l'avenant signé a bien été reçu ?")) return;
    setBusy(true);
    try {
      await enfantsApi.validerAvenant(enfantId, res?.verdict ? 'malgré l\'analyse automatique' : '');
      setRes({ type: 'ok', text: 'Avenant marqué comme signé.' });
      load(); onValide && onValide();
    } catch (err) { setRes({ type: 'error', text: err.response?.data?.error || 'Validation impossible.' }); }
    setBusy(false);
  };

  if (!attente) {
    return res?.type === 'ok'
      ? <div className="text-[12.5px] rounded px-3 py-2 bg-emerald-50 text-emerald-700 flex items-center gap-2"><CheckCircle2 size={15} /> {res.text}</div>
      : null;
  }

  const depuis = (attente.date_echeance || '').split('-').reverse().join('/');
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2.5">
      <div className="flex items-start gap-2 text-[13px] text-amber-800">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Avenant à faire signer{depuis ? ` (depuis le ${depuis})` : ''}</div>
          <div className="text-[12.5px]">{attente.libelle}</div>
          <div className="text-[12px] text-amber-700 mt-0.5">Ce rappel reste affiché jusqu'à réception de l'avenant signé : signature électronique, ou dépôt du document signé (vérifié automatiquement).</div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <a href={enfantsApi.getAvenantPdfUrl(enfantId)} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs"><FileText size={14} /> Générer l'avenant</a>
        <EnvoiDocumentButton type="avenant" enfantId={enfantId} refId={enfantId} destinataireDefaut={emailFamille} label="Envoyer" />
        <SignatureButton type="avenant" refId={enfantId} email={emailFamille} nom={nomFamille} />
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className="btn-primary text-xs">
          {busy ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />} {busy ? 'Vérification…' : "Déposer l'avenant signé"}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={e => deposer(e.target.files?.[0])} />
        <button type="button" disabled={busy} onClick={validerManuellement} className="text-xs text-amber-800 underline">Valider manuellement</button>
      </div>
      {res && (
        <div className={`text-[12.5px] rounded px-3 py-2 flex items-start gap-2 ${res.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-white text-red-600 border border-red-100'}`}>
          {res.type === 'ok' ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" /> : <XCircle size={15} className="flex-shrink-0 mt-0.5" />}
          <span>{res.text}{res.verdict?.resume ? <><br /><span className="text-gray-500">Analyse : {res.verdict.resume}</span></> : null}</span>
        </div>
      )}
    </div>
  );
}
